import React, { useEffect, useMemo, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useAuth } from "../../../../../context/AuthContext";
import ProfilePhotoControl from "../../../UserDashboard/ProfilePhotoControl";
import MySubscriptionSection from "../../../UserDashboard/MySubscriptionSection";
import "./AccountTab.css";

const MY_CAPTAIN_PROFILE = gql`
  query MyCaptainProfile {
    myCaptainProfile {
      id
      displayName
      bio
      tagline
      joinDate
    }
  }
`;


const UPSERT_MY_CAPTAIN_PROFILE = gql`
  mutation UpsertMyCaptainProfile($input: MyCaptainProfileInput!) {
    upsertMyCaptainProfile(input: $input) {
      id
      displayName
      bio
      tagline
      joinDate
    }
  }
`;

// Self-service editor for the caller's row in the captain showcase.
// Captain-or-above only; backend gates both query + mutation. First
// save auto-creates the row with linkedSocialUserId + linkedEmail
// pulled from the JWT, so the admin doesn't have to spin up a
// placeholder row in the Captains admin tab.
const MyShowcaseEntryEditor = () => {
  const { data, loading, error, refetch } = useQuery(MY_CAPTAIN_PROFILE, {
    fetchPolicy: "network-only",
  });
  const [upsert, { loading: saving }] = useMutation(UPSERT_MY_CAPTAIN_PROFILE);
  const profile = data?.myCaptainProfile || null;

  const [displayName, setDisplayName] = useState("");
  const [joinDate, setJoinDate] = useState("");
  const [tagline, setTagline] = useState("");
  const [bio, setBio] = useState("");
  const [saveError, setSaveError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  // Sync local state when the loaded profile changes (initial load
  // or after a save that returns the fresh row).
  useEffect(() => {
    setDisplayName(profile?.displayName || "");
    setJoinDate(profile?.joinDate || "");
    setTagline(profile?.tagline || "");
    setBio(profile?.bio || "");
  }, [profile?.id, profile?.displayName, profile?.joinDate, profile?.tagline, profile?.bio]);

  const handleSave = async () => {
    setSaveError("");
    try {
      await upsert({
        variables: {
          input: { displayName, joinDate, tagline, bio },
        },
      });
      setSavedAt(new Date());
      refetch();
    } catch (err) {
      setSaveError(err.message || "Save failed");
    }
  };

  if (loading && !profile) {
    return (
      <div className="account-debug" style={{ marginTop: 16 }}>
        <p className="account-debug__row">Loading your showcase entry…</p>
      </div>
    );
  }
  if (error) {
    // Captain gate failed (regular user) or transport error. Hide
    // the section entirely if it's a forbidden error -- regular
    // users shouldn't see this UI at all.
    if (/Forbidden|captain/.test(error.message || "")) return null;
    return (
      <div
        className="account-debug"
        style={{ marginTop: 16, color: "#b00020" }}
      >
        Couldn't load showcase entry: {error.message}
      </div>
    );
  }

  return (
    <div className="account-debug" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>My Captain Showcase Entry</h3>
      <p className="account-debug__row" style={{ color: "#555" }}>
        {profile
          ? "Your name + bio shown on the captains story strip. Save to publish edits."
          : "You're not in the captains showcase yet. Fill this in and Save to add yourself."}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <strong>Display name</strong>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="How your name should appear"
            style={{ padding: 8, fontSize: 14 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <strong>Join date</strong>
          <input
            type="text"
            value={joinDate}
            onChange={(e) => setJoinDate(e.target.value)}
            placeholder='Free-form — e.g. "Jan 2024" or "Since the start"'
            style={{ padding: 8, fontSize: 14 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <strong>Tagline (optional)</strong>
          <input
            type="text"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder='Short badge text — e.g. "Founder", "Pickleball captain"'
            style={{ padding: 8, fontSize: 14 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <strong>Bio</strong>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="A few sentences about yourself for the detail card."
            rows={5}
            style={{ padding: 8, fontSize: 14, resize: "vertical" }}
          />
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "8px 16px",
              background: "#4a89dc",
              color: "white",
              border: "none",
              borderRadius: 6,
              fontSize: 14,
              cursor: saving ? "default" : "pointer",
              fontWeight: 600,
            }}
          >
            {saving ? "Saving…" : profile ? "Save changes" : "Add me to showcase"}
          </button>
          {savedAt && !saving && (
            <span style={{ color: "#1f7a3a", fontSize: 13 }}>
              Saved {savedAt.toLocaleTimeString()}
            </span>
          )}
          {saveError && (
            <span style={{ color: "#b00020", fontSize: 13 }}>{saveError}</span>
          )}
        </div>
        <p className="account-debug__row" style={{ color: "#888", fontSize: 12 }}>
          The admin controls sort order, profile photo (for your captain card),
          and the active/hidden flag from the Captains admin tab. Self-edit
          covers only the four fields above.
        </p>
      </div>
    </div>
  );
};

// Decode a base64url JWT payload without verifying — purely for the
// debug section below. Never trust the contents for any access
// decision; the backend already verifies the signature on every call.
function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

const AccountTab = () => {
  const { userInfo, userRole, logout } = useAuth();

  // Pull the live ID token straight from sessionStorage and decode it
  // so we can show the operator exactly what the server is going to
  // see. Useful for "I was promoted to Captain but the UI says I'm
  // not" debugging — if cognito:groups doesn't include "Captains"
  // here, the JWT is stale and a fresh login is needed.
  const tokenInfo = useMemo(() => {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const token = window.sessionStorage.getItem("cognitoIdToken");
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    if (!payload) return { error: "Token present but could not be decoded." };
    const groupsRaw = payload["cognito:groups"];
    const groups = Array.isArray(groupsRaw)
      ? groupsRaw
      : typeof groupsRaw === "string" && groupsRaw.length
      ? groupsRaw.split(/[\s,]+/).filter(Boolean)
      : [];
    const exp = payload.exp ? new Date(payload.exp * 1000) : null;
    const iat = payload.iat ? new Date(payload.iat * 1000) : null;
    return {
      email: payload.email || null,
      sub: payload.sub || null,
      groups,
      tokenUse: payload.token_use || null,
      issuedAt: iat,
      expiresAt: exp,
      isExpired: exp ? exp.getTime() < Date.now() : null,
    };
  }, [userInfo]); // recompute when AuthContext updates

  return (
    <div className="account-section">
      <div className="user-profile">
        <h2>Account Settings</h2>
        {userInfo && userInfo.sub && (
          <ProfilePhotoControl
            userId={userInfo.sub}
            email={userInfo.email}
            name={userInfo.name || userInfo.email}
          />
        )}
        {userInfo && (
          <div className="account-details">
            <p className="account-email">
              <strong>Email:</strong> {userInfo.email}
            </p>
            <p className="account-role">
              <strong>Role (frontend):</strong> {userRole}
            </p>
          </div>
        )}

        <MySubscriptionSection />

        <MyShowcaseEntryEditor />

        {/* Your access — diagnostic. Collapsed by default since most
            of the time the operator just wants Email + Role at the
            top. Tap to expand when troubleshooting "I was promoted
            but the UI says no" — Captains missing from groups below
            means the JWT is stale and a fresh sign-in (or storage
            clear) will fix it. */}
        <details className="account-debug">
          <summary>
            <strong>Show technical details (Cognito / JWT)</strong>
          </summary>
          {!tokenInfo && (
            <p className="account-debug__row">
              No ID token in session storage — you're not signed in.
            </p>
          )}
          {tokenInfo && tokenInfo.error && (
            <p className="account-debug__row">{tokenInfo.error}</p>
          )}
          {tokenInfo && !tokenInfo.error && (
            <>
              <p className="account-debug__row">
                <strong>Cognito sub:</strong>{" "}
                <code>{tokenInfo.sub || "—"}</code>
              </p>
              <p className="account-debug__row">
                <strong>cognito:groups:</strong>{" "}
                {tokenInfo.groups.length === 0 ? (
                  <em>(none — only baseline signed-in access)</em>
                ) : (
                  tokenInfo.groups.map((g) => (
                    <code
                      key={g}
                      className={
                        g === "Admins"
                          ? "account-debug__group account-debug__group--admin"
                          : g === "Captains"
                          ? "account-debug__group account-debug__group--captain"
                          : "account-debug__group"
                      }
                    >
                      {g}
                    </code>
                  ))
                )}
              </p>
              <p className="account-debug__row">
                <strong>Token use:</strong> {tokenInfo.tokenUse || "—"}
              </p>
              <p className="account-debug__row">
                <strong>Issued:</strong>{" "}
                {tokenInfo.issuedAt
                  ? tokenInfo.issuedAt.toLocaleString()
                  : "—"}
              </p>
              <p className="account-debug__row">
                <strong>Expires:</strong>{" "}
                {tokenInfo.expiresAt
                  ? tokenInfo.expiresAt.toLocaleString()
                  : "—"}
                {tokenInfo.isExpired && (
                  <em> — expired; sign in again</em>
                )}
              </p>
            </>
          )}
          <p className="account-debug__hint">
            If you were just promoted to Captain but don't see <code>Captains</code> above,
            this browser is using a cached token from before the promotion. Sign out, then
            sign in again (or clear browser storage / use a private tab) to pick up the
            new group.
          </p>
        </details>

        <button onClick={logout} className="logout-btn">
          Logout
        </button>
      </div>
    </div>
  );
};

export default AccountTab;
